type WidgetConfig = {
  cid: string;
  aid: string;
  apiBase: string;
  elEmbedUrl: string;
};

const SCRIPT_SELECTOR = 'script[data-cid][data-aid]';
const HOST_ATTR = 'data-fp-chat-host';
const EL_CUSTOM_ELEMENT = 'elevenlabs-convai';

(function bootstrap() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (document.querySelector(`[${HOST_ATTR}]`)) return;

  const scriptEl =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>(SCRIPT_SELECTOR);
  if (!scriptEl) {
    console.warn('[fp-chat] could not locate widget script tag');
    return;
  }

  // Opaque random base62 tokens — stored on CompanyRecord.Cid +
  // AgentRecord.Aid at create / sync time. Plain company / agent ids
  // deliberately don't appear in customer-side markup; the backend
  // resolves the tokens and enforces that the agent actually belongs
  // to that company before handing back an ElevenLabs provider_agent_id.
  const cid = scriptEl.dataset.cid?.trim();
  const aid = scriptEl.dataset.aid?.trim();
  if (!cid || !aid) {
    console.warn('[fp-chat] missing data-cid / data-aid');
    return;
  }

  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  const elEmbedUrl = resolveEmbedUrl(
    (import.meta.env.VITE_EL_EMBED_URL as string | undefined) ??
      'https://unpkg.com/@elevenlabs/convai-widget-embed',
    scriptEl
  );

  if (!apiBase) {
    console.warn('[fp-chat] VITE_API_BASE not set at build time');
    return;
  }

  void boot({ cid, aid, apiBase, elEmbedUrl });
})();

async function boot(cfg: WidgetConfig): Promise<void> {
  const providerAgentId = await resolveAgent(cfg);
  if (!providerAgentId) return;

  const host = document.createElement('div');
  host.setAttribute(HOST_ATTR, '');
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'closed' });

  try {
    await loadElevenLabsEmbed(cfg.elEmbedUrl);
  } catch (err) {
    console.warn('[fp-chat] failed to load chat engine', err);
    host.remove();
    return;
  }

  const widget = document.createElement(EL_CUSTOM_ELEMENT);
  widget.setAttribute('agent-id', providerAgentId);
  shadow.appendChild(widget);
}

async function resolveAgent(cfg: WidgetConfig): Promise<string | null> {
  try {
    const url = `${cfg.apiBase}/public/chat/agents/${encodeURIComponent(cfg.cid)}/${encodeURIComponent(cfg.aid)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      console.warn('[fp-chat] resolve failed:', res.status);
      return null;
    }
    const body = (await res.json()) as { provider_agent_id?: string };
    return body.provider_agent_id ?? null;
  } catch (err) {
    console.warn('[fp-chat] resolve error:', err);
    return null;
  }
}

// Deployed builds bake an absolute embed URL: optimization plugins such as WP
// Rocket re-host widget.js on the customer's domain, where a relative path would
// resolve to a file that doesn't exist. A relative path (local builds) resolves
// against the origin that served widget.js; inline usage without a src falls
// back to page-relative resolution (dev sandbox).
function resolveEmbedUrl(embedUrl: string, scriptEl: HTMLScriptElement): string {
  try {
    return new URL(embedUrl, scriptEl.src || document.baseURI).href;
  } catch {
    return embedUrl;
  }
}

let elEmbedPromise: Promise<void> | null = null;
function loadElevenLabsEmbed(src: string): Promise<void> {
  if (window.customElements.get(EL_CUSTOM_ELEMENT)) {
    return Promise.resolve();
  }
  if (elEmbedPromise) return elEmbedPromise;

  elEmbedPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.type = 'text/javascript';
    s.onload = () => {
      window.customElements.whenDefined(EL_CUSTOM_ELEMENT).then(() => resolve());
    };
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });

  return elEmbedPromise;
}
